import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { ArbetsorderAtgardDTO } from "~app/common/models/arbetsorder/atgard/ArbetsorderAtgardDTO";
import { FeatureSettingDTO } from "~app/common/models/featureSetting/FeatureSettingDTO";

import { FeatureSettingUserDTO } from "~app/common/models/featureSetting/FeatureSettingUserDTO";
import { OmkorningRelationerLagertransaktionRequestDTO } from "~app/common/models/matning/OmkorningRelationerLagertransaktionRequestDTO";
import { TolkningOmsandningResponseDTO } from "~app/common/models/matning/TolkningOmsandningResponseDTO";
import { ProduktinstruktionUpdateResponseDTO } from "~app/common/models/produkt/ProduktinstruktionUpdateResponseDTO";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { VirkeskopsatgardDTO } from "~app/common/models/virkeskop/VirkeskopKontraktAvtalResponseDTO";

import { SearchAvtalByAnvandareRequestDTO } from "~app/common/models/virkeskop/v2/SearchAvtalByAnvandareRequestDTO";
import { SearchAvtalByAnvandareResponseDTO } from "~app/common/models/virkeskop/v2/SearchAvtalByAnvandareResponseDTO";
import { ApiClientService } from "~app/common/services/api-client.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Avverkningsanmalan } from "./tabs/sysadm-avverkningsanmalan/avverkningsanmalan.model";
import { IVirkeskopNvb } from "./tabs/sysadm-misc/sysadm-misc.component";

@Injectable({
    providedIn: "root"
})
export class SystemadministrationService {
    private readonly apiClient = inject(ApiClientService);

    updateAvverkningsanmalan(request: string): Observable<Avverkningsanmalan> {
        return this.apiClient.post(`arbetsorder/${request}/updateavverkningsanmalan`, true);
    }

    getAllProduktinstruktioner(): Observable<ProduktinstruktionUpdateResponseDTO> {
        return this.apiClient.post(`Produkt/GetAllProduktinstruktioner`, {})
            .pipe(map((response: any) => response.data as ProduktinstruktionUpdateResponseDTO));
    }

    loadFeatureSettings(): Observable<FeatureSettingDTO[]> {
        return this.apiClient.get("Feature/");
    }

    loadUserFeatureSettings(): Observable<FeatureSettingUserDTO[]> {
        return this.apiClient.get("Feature/userFeatures");
    }

    saveFeatureSettings(featureSettings: FeatureSettingDTO[]): Observable<FeatureSettingDTO[]> {
        const data = { featureSettingList: featureSettings };
        return this.apiClient.post(`Feature/saveFeatures`, data);
    }

    omkorningRelationerLagertransaktion(request: OmkorningRelationerLagertransaktionRequestDTO): Observable<TolkningOmsandningResponseDTO> {
        return this.apiClient.post(`matning/omkorning-leverans-redovisningsdatum`, request);
    }

    updateLagersaldo(lagerplatsId: string) {
        if (lagerplatsId === undefined) {
            lagerplatsId = "";
        }

        return this.apiClient.get(`Lagersaldo/UpdateFromProlog/${lagerplatsId}`, true);
    }

    getVacsXmlVirkeskop(kontraktsnummer: string): Observable<string> {
        return this.apiClient.get(`VACSKontrakt/Virkeskop/${kontraktsnummer}/xml`);
    }

    getVacsXmlArbetsorder(beteckning: string): Observable<string> {
        return this.apiClient.get(`VACSKontrakt/Arbetsorder/${beteckning}/xml`);
    }

    arbetsorderDistriktOmkorning() {
        return this.apiClient.post(`cqs/schedulecommand`, { $type: "BasladdaArbersorderDistriktCommand" });
    }

    query<T>(query: any): Observable<T> {
        return this.apiClient.post(`cqs/query`, query);
    }

    userQuery<T>(query: any): Observable<T> {
        return this.apiClient.post(`cqs/userQuery`, query);
    }

    scheduleCommand(cmd: { $type: string }, antalForsok = 1, nyckel1 = null, meddelande = null) {
        const params = new URLSearchParams({ antalForsok: antalForsok.toString() });
        if (ObjectUtils.stringHasValue(nyckel1)) {
            params.append("nyckel1", nyckel1);
        }
        if (ObjectUtils.stringHasValue(meddelande)) {
            params.append("meddelande", meddelande);
        }
        return this.apiClient.post(`cqs/schedulecommand?${params.toString()}`, cmd);
    }

    scheduleUserCommand(cmd: { $type: string }, antalForsok = 1, nyckel1 = null, meddelande = null) {
        const params = new URLSearchParams({ antalForsok: antalForsok.toString() });
        if (ObjectUtils.stringHasValue(nyckel1)) {
            params.append("nyckel1", nyckel1);
        }
        if (ObjectUtils.stringHasValue(meddelande)) {
            params.append("meddelande", meddelande);
        }
        return this.apiClient.post(`cqs/scheduleusercommand?${params.toString()}`, cmd);
    }

    getRensaEjLevvKontraktNvb(): Observable<IVirkeskopNvb[]> {
        return this.apiClient.get<IVirkeskopNvb[]>("administration/nvbadministration/GetRensaEjLevvKontraktNvb", false);
    }

    getRensaLevvKontraktNvb(): Observable<IVirkeskopNvb[]> {
        return this.apiClient.get<IVirkeskopNvb[]>("administration/nvbadministration/GetRensaLevvKontraktNvb", false);
    }

    getLeveransJson(kontraktsnummer: string): Observable<string> {
        return this.apiClient.get(`matning/leverans/${kontraktsnummer}/json`);
    }

    rensaKontraktNvb() {
        return this.apiClient.post("administration/nvbadministration/RensaKontraktNvb", {});
    }

    searchAnvandare(search: string) {
        return this.apiClient.post<any, ITypeAheadResult[]>("typeahead/anvandare", { query: search }, false, null, ApiClientService.JsonHeader);
    }

    searchAvtal(searchRequest: SearchAvtalByAnvandareRequestDTO) {
        return this.apiClient.get<SearchAvtalByAnvandareResponseDTO[]>(
            "virkeskopsavtal/admin/search", false, searchRequest);
    }

    moveAvtal(fromUserId: number, toUserId: number, avtalIds: number[]) {
        return this.apiClient.post<any, any[]>(
            "virkeskopsavtal/admin/move",
            {
                fromAnvandareId: fromUserId,
                toAnvandareId: toUserId,
                avtalIds: avtalIds
            },
            false);
    }

    getVirkeskopsatgard(beteckning: string): Observable<VirkeskopsatgardDTO> {
        return this.apiClient.get<VirkeskopsatgardDTO>(`virkeskopsatgard/beteckning/${beteckning}`, false);
    }

    getArbetsordersatgard(beteckning: string): Observable<ArbetsorderAtgardDTO> {
        return this.apiClient.get<ArbetsorderAtgardDTO>(`arbetsorder/beteckning/${beteckning}`, false);
    }

    utbyteSimuleraArbetsorder(id: number) {
        return this.apiClient.post(`arbetsorder/${id}/simulera`, {}, false);
    }

    makeValid(atgid: number, feature: string, method: string, meters: number) {
        switch (feature) {
            case "virkeskop":
                return this.apiClient.post(`cqs/command`, {
                    $type: "VirkeskopAtgardGeometriMakeValidMessageCommand",
                    atgardId: atgid,
                    method,
                    meters
                });
            case "arbetsorder":
                return this.apiClient.post(`cqs/command`, {
                    $type: "ArbetsorderAtgardGeometriMakeValidMessageCommand",
                    atgardId: atgid,
                    method,
                    meters
                });
        }
        return undefined;
    }

    validateMomsnummer(momsnummer: string): Observable<any> {
        return this.apiClient.get(`virkesleverantor/validatemomsnummer`, false, { momsnummer: momsnummer });
    }

    validateMomsnummerForAllVirkesleverantorer(): Observable<string> {
        return this.apiClient.post<unknown, string>(`cqs/command`, { $type: "ValidateMomsnummerForAllVirkesleverantorerCommand" });
    }
}
